// ─────────────────────────────────────────────────────────────────────────────
// Analytics Service
// Serviço CENTRAL de tracking GA4 (pipeline JS único: web + Android + iOS).
// Navegação: page_view (mesmo nome/params). Sem setScreenName / screen_view nativo.
// Transporte: FirebaseAnalytics.logEvent (Capacitor) ou gtag.js (web prod sem appId).
// Todos os eventos passam por aqui. Execução silenciosa (fire-and-forget).
// ─────────────────────────────────────────────────────────────────────────────

import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';

import {
  getFirebaseAnalyticsProfile,
  getFirebaseConfig,
  isCatalogBrandContextPath,
  PAGE_VIEW_DEDUP_MS,
  SUGGESTED_CLICK_TTL_MS,
  SUGGESTED_LIST,
  suggestedListPlacement,
} from './analytics.config';
import { AnalyticsMapperService } from './analytics-mapper.service';
import { PromoAttributionService } from './promo-attribution.service';
import {
  AnalyticsCheckoutOptions,
  CLUBE_PAYMENT_TYPE,
  ClubePaymentType,
  GaItem,
  PURCHASE_ORIGIN_CLUBE,
  PURCHASE_ORIGIN_LOJA,
  PurchaseOrigin,
  SuggestedListContext,
  UiMessageParams,
} from './analytics.types';

import { Product } from '@app/models/product';
import { CartItem } from '@app/models/cart';
import { Banner } from '@app/models/banner';
import { Delivery } from '@app/models/delivery';
import { Payment } from '@app/models/payment';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {

  private initialized = false;
  private readonly platform = Capacitor.getPlatform(); // 'web' | 'ios' | 'android'
  /** Web prod: gtag.js quando appId Firebase web ainda não foi registrado. */
  private webGtagMode = false;
  private webMeasurementId = '';

  /** Estado de deduplicação do page_view (único evento de navegação nos 3 ambientes). */
  private lastPageViewPath = '';
  private lastPageViewTime = 0;

  /** Rota e tela atuais (contexto para eventos). */
  currentRoute = '';
  currentScreen = '';

  /**
   * Cliques recentes em cards do Produto Sugerido (chave do produto → lista).
   * O card leva o usuário para o detalhe/modal e a adição acontece por lá: sem
   * essa memória o add_to_cart originado da recomendação chegaria ao GA4 sem
   * lista nenhuma e o funil exibição > clique > compra não fecharia.
   */
  private readonly suggestedClicks = new Map<string, { list: SuggestedListContext; at: number }>();

  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private _cartRef: any;

  constructor(
    private readonly mapper: AnalyticsMapperService,
    private readonly promoAttribution: PromoAttributionService,
  ) {
    this.mapper.catalogContextResolver = () => ({
      category: this.cart?.currentCategory?.label,
      brand: isCatalogBrandContextPath(this.currentRoute)
        ? this.cart?.currentBrand?.label
        : undefined,
    });
    this.mapper.suggestedCartItemResolver = (cartItem) => (
      this.cart?.isSuggestedCartItem?.(cartItem) ?? false
    );
    this.mapper.catalogProductResolver = (keys) => this.findCatalogProduct(keys);
  }

  /**
   * Reencontra o Product no catálogo em memória a partir das chaves do item.
   * Fallback para o cache de facetas quando o item entrou no carrinho antes
   * de o cache existir (ex.: carrinho server-side vindo de outra sessão).
   */
  private findCatalogProduct(keys: string[]): Product | undefined {
    if (!keys?.length) return undefined;
    const wanted = new Set(keys);
    try {
      const pools: (Product[] | undefined)[] = [
        this.cart?.currentProduct ? [this.cart.currentProduct] : undefined,
        this.cart?.products?.(),
        this.cart?.promotions?.(),
        this.cart?.recommendedProducts?.(),
        this.cart?.recommendedPointsProducts?.(),
        this.cart?.allPromotions,
      ];
      for (const pool of pools) {
        const found = pool?.find((p) => this.mapper.productKeys(p).some((k) => wanted.has(k)));
        if (found) return found;
      }
    } catch { /* silencioso */ }
    return undefined;
  }

  /** Resolve CartService lazy para evitar dependência circular. */
  private get cart(): any {
    if (!this._cartRef) {
      try {
        const { CartService } = require('@app/services/cart.service');
        this._cartRef = this.injector.get(CartService);
      } catch { /* fallback silencioso */ }
    }
    return this._cartRef;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Inicialização
  // ═══════════════════════════════════════════════════════════════════════════

  async init(): Promise<void> {
    if (this.initialized) return;

    if (this.platform === 'web') {
      const cfg = getFirebaseConfig();
      if (!cfg.measurementId) {
        console.warn('[Analytics] measurementId ausente — analytics web desabilitado.');
      } else if (cfg.appId) {
        console.log(
          '%c[GA4] Inicializando Firebase Analytics (web)',
          'color: #4285F4; font-weight: bold;',
          {
            profile: getFirebaseAnalyticsProfile(),
            label: cfg.label,
            projectId: cfg.projectId,
            measurementId: cfg.measurementId,
            ga4PropertyId: cfg.ga4PropertyId,
          },
        );
        await FirebaseAnalytics.initializeFirebase(cfg as any);
        // Desliga page_view automático do Firebase Web — trackRoute() envia os da SPA.
        try {
          const analyticsRef = (window as any)?.firebase?.analytics?.();
          analyticsRef?.setConfig?.({ send_page_view: false });
        } catch { /* ignore */ }
      } else {
        console.log(
          '%c[GA4] Inicializando gtag.js (web — produção)',
          'color: #4285F4; font-weight: bold;',
          {
            profile: getFirebaseAnalyticsProfile(),
            label: cfg.label,
            measurementId: cfg.measurementId,
            ga4PropertyId: cfg.ga4PropertyId,
          },
        );
        await this.initWebGtag(cfg.measurementId);
      }
    }

    this.initialized = true;
    console.log(
      `%c[GA4] Analytics inicializado — plataforma: ${this.platform}`,
      'color: #34A853; font-weight: bold;',
    );
  }

  /** Bootstrap gtag.js para web prod (Measurement ID sem Firebase Web App ID). */
  private async initWebGtag(measurementId: string): Promise<void> {
    if (typeof document === 'undefined') {
      return;
    }

    // Padrão oficial do gtag: configurar dataLayer + stub + config ANTES de baixar o
    // script. Os eventos ficam em buffer no dataLayer e são enviados quando o gtag.js
    // carrega — então uma falha/lentidão transitória no download NÃO trava o analytics.
    window.dataLayer = window.dataLayer ?? [];
    // CRÍTICO: o gtag.js só reconhece entradas do dataLayer que são objetos `arguments`
    // (ele checa `[object Arguments]` / `callee`). Um array puro é IGNORADO — era por isso
    // que config/event nunca viravam hit no /collect. Empilhar `arguments`, nunca [...args].
    window.gtag = window.gtag ?? function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    // Desliga page_view automático: as rotas Angular disparam via trackRoute().
    window.gtag('config', measurementId, { send_page_view: false });

    this.webGtagMode = true;
    this.webMeasurementId = measurementId;

    // Carrega a biblioteca fire-and-forget: não bloqueia o init nem a UI. Se falhar
    // agora, os eventos seguem enfileirados no dataLayer para envio quando carregar.
    this.loadScript(
      `https://www.googletagmanager.com/gtag/js?id=${measurementId}`,
      'ga4-gtag',
    ).catch((err) => {
      console.warn('[Analytics] gtag.js não carregou agora (eventos em buffer no dataLayer):', err);
    });
  }

  private loadScript(src: string, id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.id = id;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Falha ao carregar script: ${src}`));
      document.head.appendChild(script);
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Navegação: page_view único via JS (web + Android + iOS)
  // Um só evento/params nos 3 ambientes — sem setScreenName / screen_view nativo.
  // ═══════════════════════════════════════════════════════════════════════════

  /** Rastreia navegação Angular. Mesmo page_view em web, Android e iOS. */
  trackRoute(url: string): void {
    const path = (url || '').split('?')[0] || '/';
    const screenName = this.routeToScreenName(path);

    this.currentRoute = path;
    this.currentScreen = screenName;

    const now = Date.now();
    if (path === this.lastPageViewPath && now - this.lastPageViewTime < PAGE_VIEW_DEDUP_MS) {
      return;
    }

    if (typeof document !== 'undefined') {
      document.title = `Orbitta | ${screenName}`;
    }

    const pageParams: Record<string, any> = {
      page_path: path,
      page_location: typeof window !== 'undefined' ? window.location.href : path,
      page_title: screenName,
    };
    if (path.includes('/catalog') || path.includes('/pp/')) {
      const category = this.cart?.currentCategory?.label;
      if (category) pageParams['page_category'] = category;
      if (isCatalogBrandContextPath(path)) {
        const brand = this.cart?.currentBrand?.label;
        if (brand) pageParams['page_brand'] = brand;
      }
      if (path.includes('/details')) {
        const product = this.cart?.currentProduct;
        if (product) {
          pageParams['product_id'] = product.sku || product.id || '';
          pageParams['product_name'] = product.name || product.label || '';
        }
      }
    }

    this.log('page_view', pageParams);
    this.lastPageViewPath = path;
    this.lastPageViewTime = now;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Identificacao do usuario
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Identifica o usuario no GA4. Chamar apos login bem-sucedido.
   * Unifica sessoes pre e pos-login no mesmo usuario.
   *
   * PII: NUNCA enviar nome/razao social/e-mail/telefone daqui. Os Termos do
   * Google proibem dados atrelaveis a uma pessoa e a conta pode ser bloqueada.
   * Por isso a antiga user property `trading_name` foi removida — no lugar vai
   * `internal_user_id`, o mesmo codigo de cliente ja usado em user_id, que e
   * um identificador interno e permite o join com o BigQuery.
   */
  async identify(user: { clientId?: string }): Promise<void> {
    try {
      if (!this.initialized) await this.init();

      const userId = user.clientId ?? '';
      if (!userId) return;

      if (this.webGtagMode && this.webMeasurementId && typeof window !== 'undefined' && window.gtag) {
        window.gtag('config', this.webMeasurementId, { user_id: userId });
        window.gtag('set', 'user_properties', { internal_user_id: userId });
      } else {
        await FirebaseAnalytics.setUserId({ userId });
        await FirebaseAnalytics.setUserProperty({ name: 'internal_user_id', value: userId });
      }
      console.log(
        `%c[GA4] setUserId: ${userId}`,
        'color: #34A853; font-weight: bold;',
      );
    } catch (err) {
      console.warn('[Analytics] identify falhou:', err);
    }
  }

  /** Limpa identificacao (logout). */
  async clearIdentity(): Promise<void> {
    try {
      if (!this.initialized) await this.init();
      if (this.webGtagMode && this.webMeasurementId && typeof window !== 'undefined' && window.gtag) {
        window.gtag('config', this.webMeasurementId, { user_id: undefined });
        window.gtag('set', 'user_properties', { internal_user_id: null });
      } else {
        await FirebaseAnalytics.setUserId({ userId: '' });
        await FirebaseAnalytics.setUserProperty({ name: 'internal_user_id', value: '' });
      }
      console.log(
        '%c[GA4] clearIdentity (userId limpo)',
        'color: #EA4335; font-weight: bold;',
      );
    } catch (err) {
      console.warn('[Analytics] clearIdentity falhou:', err);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Eventos Padrão GA4 — E-commerce
  // ═══════════════════════════════════════════════════════════════════════════

  /** Login bem-sucedido. */
  trackLogin(method: string = 'password'): void {
    this.log('login', { method });
  }

  /** Exibição dos resultados de busca. */
  trackViewSearchResults(searchTerm: string): void {
    this.log('view_search_results', { search_term: searchTerm });
  }

  /** Visualização de uma lista de produtos. */
  trackViewItemList(
    listId: string,
    listName: string,
    products: Product[],
    extra?: { category?: string; brand?: string; flavor?: string },
  ): void {
    const params: Record<string, any> = {
      item_list_id: listId,
      item_list_name: listName,
      items: this.mapper.productsToGaItems(products),
    };
    if (extra?.category) params['list_category'] = extra.category;
    if (extra?.brand) params['list_brand'] = extra.brand;
    if (extra?.flavor) params['list_flavor'] = extra.flavor;
    this.log('view_item_list', params);
  }

  /** Clique em um item da lista. */
  trackSelectItem(product: Product, listId: string, listName: string, index?: number): void {
    this.log('select_item', {
      item_list_id: listId,
      item_list_name: listName,
      items: [this.mapper.productToGaItem(product, undefined, index)],
    });
  }

  /** Exibição da seção Produto Sugerido. */
  trackViewSuggestedProducts(products: Product[], isPointsProgram: boolean = false, pagePath = this.placementPath()): void {
    if (!products?.length) return;
    if (!suggestedListPlacement(pagePath)) return;
    const list = this.getSuggestedListContext(isPointsProgram, pagePath);
    const items = this.mapper.productsToGaItems(products).map((item) => this.withSuggestedItemContext(item, list));
    this.log('view_item_list', {
      item_list_id: list.id,
      item_list_name: list.name,
      recommendation_source: list.source,
      page_path: pagePath,
      catalog_origin: isPointsProgram ? 'Clube Orbitta' : 'Loja',
      page_location: typeof window !== 'undefined' ? new URL(pagePath, window.location.origin).href : pagePath,
      page_title: this.routeToScreenName(pagePath),
      items,
    });
  }

  /** Clique em produto da seção Produto Sugerido. */
  trackSelectSuggestedProduct(product: Product, isPointsProgram: boolean = false): void {
    const list = this.getSuggestedListContext(isPointsProgram);
    const item = this.withSuggestedItemContext(this.mapper.productToGaItem(product), list);
    this.rememberSuggestedClick(product, list);
    this.log('select_item', {
      item_list_id: list.id,
      item_list_name: list.name,
      recommendation_source: list.source,
      items: [item],
    });
  }

  /** Visualização da ficha do produto. */
  trackViewItem(product: Product): void {
    const item = this.mapper.productToGaItem(product, 1);
    this.log('view_item', {
      currency: this.mapper.currency,
      value: item.price ?? 0,
      items: [item],
    });
  }

  /** Adição de produto ao carrinho. */
  trackAddToCart(product: Product, quantity: number = 1): void {
    // Adição feita fora da vitrine, mas logo após o clique no card sugerido
    // (detalhe do produto / modal de escalonado): a lista que gerou o clique
    // é quem leva o crédito, como manda a atribuição de lista do GA4.
    const clicked = this.takeSuggestedClick(product);
    if (clicked) {
      this.trackSuggestedAddToCart(product, quantity, clicked);
      return;
    }

    const item = this.mapper.productToGaItem(product, quantity);
    this.bindPromoToProduct(product);
    this.log('add_to_cart', {
      currency: this.mapper.currency,
      value: this.mapper.round((item.price ?? 0) * quantity),
      items: [item],
    });
  }

  /** Adição ao carrinho a partir da seção Produto Sugerido. */
  trackAddSuggestedProductToCart(product: Product, quantity: number = 1, isPointsProgram: boolean = false): void {
    this.takeSuggestedClick(product); // consome o clique: a adição já é da vitrine
    this.trackSuggestedAddToCart(product, quantity, this.getSuggestedListContext(isPointsProgram), isPointsProgram);
  }

  /** add_to_cart atribuído à lista de recomendação (vitrine ou clique recente nela). */
  private trackSuggestedAddToCart(
    product: Product,
    quantity: number,
    list: SuggestedListContext,
    isPointsProgram?: boolean,
  ): void {
    const item = this.withSuggestedItemContext(this.mapper.productToGaItem(product, quantity), list);
    this.bindPromoToProduct(product, isPointsProgram);
    this.rememberSuggestedList([product], list);
    this.log('add_to_cart', {
      currency: this.mapper.currency,
      value: this.mapper.round((item.price ?? 0) * quantity),
      item_list_id: list.id,
      item_list_name: list.name,
      cart_item_origin: SUGGESTED_LIST.ITEM_ORIGIN,
      recommendation_source: list.source,
      items: [item],
    });
  }

  /** Adição de múltiplos produtos ao carrinho (ex.: "Refazer última compra"). */
  trackAddToCartBulk(products: Product[]): void {
    if (!products || products.length === 0) return;
    const items = this.mapper.productsToGaItems(products);
    products.forEach((product) => this.bindPromoToProduct(product));
    const value = items.reduce(
      (sum, item, i) => sum + ((item.price ?? 0) * (products[i]?.quantity || 1)),
      0,
    );
    this.log('add_to_cart', {
      currency: this.mapper.currency,
      value: this.mapper.round(value),
      items,
    });
  }

  /**
   * Adição em massa a partir da seção Produto Sugerido ("Adicionar todos").
   * A quantidade adicionada de cada produto vem em `product.quantity`.
   */
  trackAddSuggestedProductsToCart(products: Product[], isPointsProgram: boolean = false): void {
    if (!products?.length) return;
    const list = this.getSuggestedListContext(isPointsProgram);
    const items = products.map((product, index) =>
      this.withSuggestedItemContext(this.mapper.productToGaItem(product, product.quantity || 1, index), list),
    );
    products.forEach((product) => this.bindPromoToProduct(product, isPointsProgram));
    this.rememberSuggestedList(products, list);
    const value = items.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0);
    this.log('add_to_cart', {
      currency: this.mapper.currency,
      value: this.mapper.round(value),
      item_list_id: list.id,
      item_list_name: list.name,
      cart_item_origin: SUGGESTED_LIST.ITEM_ORIGIN,
      recommendation_source: list.source,
      // Separa o "Adicionar todos" do add unitário do card (mesmo evento, ações diferentes).
      add_method: SUGGESTED_LIST.BULK_ADD_METHOD,
      items,
    });
  }

  /** Remoção de item do carrinho (ou redução de quantidade). */
  trackRemoveFromCart(product: Product, quantity: number = 1): void {
    const item = this.mapper.productToGaItem(product, quantity);
    this.log('remove_from_cart', {
      currency: this.mapper.currency,
      value: this.mapper.round((item.price ?? 0) * quantity),
      items: [item],
    });
  }

  /** Remoção em massa do carrinho (ex.: "Excluir todos os itens"). */
  trackRemoveFromCartBulk(cartItems: CartItem[]): void {
    if (!cartItems?.length) return;
    const items = this.mapper.cartItemsToGaItems(cartItems);
    const value = this.mapper.cartItemsTotal(cartItems);
    this.log('remove_from_cart', {
      currency: this.mapper.currency,
      value: this.mapper.round(value),
      items,
    });
  }

  /** Remoção/redução de item que entrou pelo Produto Sugerido. */
  trackRemoveSuggestedProductFromCart(product: Product, quantity: number = 1, isPointsProgram: boolean = false): void {
    const list = this.getSuggestedListContext(isPointsProgram);
    const item = this.withSuggestedItemContext(this.mapper.productToGaItem(product, quantity), list);
    this.log('remove_from_cart', {
      currency: this.mapper.currency,
      value: this.mapper.round((item.price ?? 0) * quantity),
      item_list_id: list.id,
      item_list_name: list.name,
      cart_item_origin: SUGGESTED_LIST.ITEM_ORIGIN,
      recommendation_source: list.source,
      items: [item],
    });
  }

  /** Visualização do carrinho. */
  trackViewCart(
    cartItems: CartItem[],
    totalValue?: number,
    purchaseOrigin: PurchaseOrigin = PURCHASE_ORIGIN_LOJA,
  ): void {
    const raw = totalValue ?? this.mapper.cartItemsTotal(cartItems);
    this.log('view_cart', {
      currency: this.mapper.currency,
      value: this.normalizeEventValue(raw),
      items: this.buildCheckoutItems(cartItems, purchaseOrigin),
      purchase_origin: purchaseOrigin,
    });
  }

  /** Início do checkout. */
  trackBeginCheckout(
    cartItems: CartItem[],
    totalValue: number,
    coupon?: string,
    purchaseOrigin: PurchaseOrigin = PURCHASE_ORIGIN_LOJA,
  ): void {
    const params: Record<string, any> = {
      currency: this.mapper.currency,
      value: this.normalizeEventValue(totalValue),
      items: this.buildCheckoutItems(cartItems, purchaseOrigin),
      purchase_origin: purchaseOrigin,
    };
    if (coupon) params['coupon'] = coupon;
    this.log('begin_checkout', params);
  }

  /** Informação de entrega selecionada. */
  trackAddShippingInfo(
    cartItems: CartItem[],
    totalValue: number,
    shippingTier: string,
    options?: AnalyticsCheckoutOptions,
  ): void {
    const purchaseOrigin = options?.purchaseOrigin ?? PURCHASE_ORIGIN_LOJA;
    const params: Record<string, any> = {
      currency: this.mapper.currency,
      value: this.normalizeEventValue(totalValue),
      shipping_tier: shippingTier,
      items: this.buildCheckoutItems(cartItems, purchaseOrigin),
      purchase_origin: purchaseOrigin,
    };
    if (options?.coupon) params['coupon'] = options.coupon;
    this.log('add_shipping_info', params);
  }

  /** Informação de pagamento selecionada. */
  trackAddPaymentInfo(
    cartItems: CartItem[],
    totalValue: number,
    paymentType: string,
    options?: AnalyticsCheckoutOptions,
  ): void {
    const purchaseOrigin = options?.purchaseOrigin ?? PURCHASE_ORIGIN_LOJA;
    const params: Record<string, any> = {
      currency: this.mapper.currency,
      value: this.normalizeEventValue(totalValue),
      payment_type: paymentType,
      items: this.buildCheckoutItems(cartItems, purchaseOrigin),
      purchase_origin: purchaseOrigin,
    };
    if (options?.coupon) params['coupon'] = options.coupon;
    this.log('add_payment_info', params);
  }

  /** Compra concluída. Uma vez por pedido. */
  trackPurchase(params: {
    transactionId: string;
    cartItems: CartItem[];
    totalValue: number;
    coupon?: string;
    paymentType?: string;
    shippingTier?: string;
    purchaseOrigin?: PurchaseOrigin;
    valuePaidReais?: number;
    valuePaidPoints?: number;
  }): void {
    const purchaseOrigin = params.purchaseOrigin ?? PURCHASE_ORIGIN_LOJA;
    const isClube = purchaseOrigin === PURCHASE_ORIGIN_CLUBE;
    const items = this.buildCheckoutItems(params.cartItems, purchaseOrigin);

    const roundedValue = this.normalizeEventValue(params.totalValue);
    const valuePaidReais = params.valuePaidReais !== undefined
      ? this.normalizeEventValue(params.valuePaidReais)
      : (isClube ? 0 : roundedValue);
    const valuePaidPoints = params.valuePaidPoints ?? 0;

    let paymentType = params.paymentType;
    if (!paymentType && isClube) {
      paymentType = AnalyticsService.getClubePaymentType(valuePaidReais, valuePaidPoints);
    }

    const eventParams: Record<string, any> = {
      transaction_id: params.transactionId,
      currency: this.mapper.currency,
      value: roundedValue,
      items: items.map(i => this.mapper.sanitizeGaItem(i)),
      purchase_origin: purchaseOrigin,
      value_paid_reais: valuePaidReais,
      value_paid_points: valuePaidPoints,
    };
    if (params.coupon) eventParams['coupon'] = params.coupon;
    if (paymentType) eventParams['payment_type'] = paymentType;
    if (params.shippingTier) eventParams['shipping_tier'] = params.shippingTier;

    this.log('purchase', this.mapper.sanitizeParams(eventParams));

    // A compra encerra a atribuição: sem isso a próxima compra da mesma sessão
    // herdaria o mesmo banner e o funil voltaria a inflar.
    this.promoAttribution.consumeItemPromos(
      (params.cartItems ?? []).map((ci) => this.mapper.cartItemKeys(ci)),
      purchaseOrigin,
    );
    this.promoAttribution.clearActivePromo(purchaseOrigin);
    this.mapper.forgetSuggestedListContexts(
      (params.cartItems ?? []).map((ci) => this.mapper.cartItemKeys(ci)),
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // E-commerce — Clube Orbitta (wrappers de compatibilidade)
  // ═══════════════════════════════════════════════════════════════════════════

  /** @deprecated Preferir trackBeginCheckout(..., PURCHASE_ORIGIN_CLUBE). */
  trackBeginCheckoutClube(cartItems: CartItem[], totalValue: number): void {
    this.trackBeginCheckout(cartItems, totalValue, undefined, PURCHASE_ORIGIN_CLUBE);
  }

  /** @deprecated Preferir trackPurchase({ purchaseOrigin: PURCHASE_ORIGIN_CLUBE, ... }). */
  trackPurchaseClube(params: {
    transactionId: string;
    cartItems: CartItem[];
    totalValue: number;
    valuePaidReais: number;
    valuePaidPoints: number;
    paymentType?: string;
    shippingTier?: string;
  }): void {
    this.trackPurchase({
      transactionId: params.transactionId,
      cartItems: params.cartItems,
      totalValue: params.totalValue,
      purchaseOrigin: PURCHASE_ORIGIN_CLUBE,
      valuePaidReais: params.valuePaidReais,
      valuePaidPoints: params.valuePaidPoints,
      paymentType: params.paymentType,
      shippingTier: params.shippingTier,
    });
  }

  /** Label legível da forma de entrega para GA4. */
  static deliveryLabel(delivery?: Delivery): string {
    if (!delivery) return 'Desconhecido';
    if (delivery.label) return delivery.label;
    if (delivery.key === 'express') return 'Expressa';
    if (delivery.key === 'conventional') return 'Padrão';
    return delivery.key || 'Desconhecido';
  }

  /** Label legível do método de pagamento da loja para GA4. */
  static lojaPaymentLabel(payment?: Payment): string {
    if (!payment) return 'Desconhecido';
    switch (payment.paymentMethod) {
      case 'pix':
        return 'PIX';
      case 'bankSlip':
        return 'Boleto';
      case 'credit':
        return 'Cartão de Crédito';
      case 'debit':
        return 'Cartão de Débito';
      case 'employees':
        return 'Funcionário';
      default:
        return payment.paymentMethodDescription || payment.paymentMethod || 'Outro';
    }
  }

  /** payment_type do Clube Orbitta conforme mix pontos + crédito. */
  static getClubePaymentType(valuePaidReais: number, valuePaidPoints: number): ClubePaymentType | undefined {
    const reais = valuePaidReais > 0;
    const pontos = valuePaidPoints > 0;
    if (pontos && !reais) return CLUBE_PAYMENT_TYPE.POINTS;
    if (reais && pontos) return CLUBE_PAYMENT_TYPE.POINTS_CREDIT;
    if (reais && !pontos) return CLUBE_PAYMENT_TYPE.CREDIT;
    return undefined;
  }

  /** @deprecated Preferir getClubePaymentType(). */
  static getClubePaymentTypeLabel(valuePaidReais: number, valuePaidPoints: number): ClubePaymentType | undefined {
    return AnalyticsService.getClubePaymentType(valuePaidReais, valuePaidPoints);
  }

  /**
   * Itens de checkout/purchase com a promoção aplicada ITEM A ITEM.
   * Só recebe promotion_id/creative_slot/creative_name o item que foi adicionado
   * ao carrinho sob um clique de banner válido — o carrinho mistura itens de
   * promoção com itens avulsos, e marcar todos inflava "items purchased in
   * promotion" muito acima de "items clicked in promotion".
   */
  private buildCheckoutItems(cartItems: CartItem[], purchaseOrigin: PurchaseOrigin): GaItem[] {
    // Fallback só para item sugerido que entrou no carrinho antes de existir a
    // memória da vitrine (ou em outra sessão): vai o nome base da lista do canal.
    const items = this.mapper.cartItemsToGaItems(
      cartItems,
      this.mapper.defaultSuggestedListContext(purchaseOrigin === PURCHASE_ORIGIN_CLUBE),
    );

    items.forEach((item, idx) => {
      const promo = this.promoAttribution.getItemPromo(
        this.mapper.cartItemKeys(cartItems?.[idx]),
        purchaseOrigin,
      );
      if (!promo) return;
      item.promotion_id = promo.promotion_id;
      item.creative_slot = promo.creative_slot;
      // promotion_name = domínio e creative_name = path da imagem do banner.
      if (promo.promotion_name) item.promotion_name = promo.promotion_name;
      if (promo.creative_name) item.creative_name = promo.creative_name;
    });

    return items;
  }

  /** Vincula o produto ao banner clicado (se houver clique dentro da janela). */
  private bindPromoToProduct(product: Product, isPointsProgram?: boolean): void {
    const origin = isPointsProgram === undefined
      ? this.currentPromoOrigin()
      : (isPointsProgram ? PURCHASE_ORIGIN_CLUBE : PURCHASE_ORIGIN_LOJA);
    this.promoAttribution.bindItemToActivePromo(this.mapper.productKeys(product), origin);
  }

  /** Origem de promoção pela rota atual (Clube vs Loja). */
  private currentPromoOrigin(): PurchaseOrigin {
    return this.currentRoute.includes('/pp/') ? PURCHASE_ORIGIN_CLUBE : PURCHASE_ORIGIN_LOJA;
  }

  /** Normaliza totais que podem vir em centavos para reais (GA4). */
  private normalizeEventValue(value: number): number {
    return this.mapper.round(this.mapper.normalizeCentavos(value));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Promoções (Banners)
  // ═══════════════════════════════════════════════════════════════════════════

  /** Impressão de banner (após 1s de visibilidade). */
  trackViewPromotion(banner: Banner, slotIndex?: number): void {
    const params = this.mapper.bannerToPromoParams(banner, slotIndex);
    this.log('view_promotion', params as any);
  }

  /** Clique em banner. */
  trackSelectPromotion(banner: Banner, slotIndex?: number): void {
    const params = this.mapper.bannerToPromoParams(banner, slotIndex);
    this.log('select_promotion', params as any);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Eventos Custom — Notificações
  // ═══════════════════════════════════════════════════════════════════════════

  /** Abertura do painel de notificações. */
  trackNotificationsOpened(): void {
    this.log('notifications_opened', {
      ui_screen: this.currentScreen,
      ui_route: this.currentRoute,
    });
  }

  /** Notificação(s) marcada(s) como lida(s). Sem id = usuário abriu o painel e leu tudo. */
  trackNotificationMarkedAsRead(notificationId?: string): void {
    this.log('notification_marked_read', {
      ...(notificationId ? { notification_id: notificationId } : {}),
      ui_screen: this.currentScreen,
      ui_route: this.currentRoute,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Eventos Custom — Mensagens de UI
  // ═══════════════════════════════════════════════════════════════════════════

  /** Exibição de mensagem de UI (toast, banner, alert). */
  trackUiMessage(params: UiMessageParams): void {
    this.log('ui_message', {
      msg_type: params.msg_type,
      msg_code: params.msg_code ?? '',
      msg_text: params.msg_text ? params.msg_text.substring(0, 100) : '',
      ui_screen: params.ui_screen ?? this.currentScreen,
      ui_route: params.ui_route ?? this.currentRoute,
      ui_component: params.ui_component ?? '',
      ...(params.ui_action ? { ui_action: params.ui_action } : {}),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Evento genérico (para extensibilidade futura)
  // ═══════════════════════════════════════════════════════════════════════════

  /** Dispara qualquer evento GA4 customizado. */
  trackCustomEvent(eventName: string, params?: Record<string, any>): void {
    this.log(eventName, params);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Internals — execução silenciosa
  // ═══════════════════════════════════════════════════════════════════════════

  /** Determina a origem do catálogo com base na rota atual. */
  private getCatalogOrigin(): string {
    return this.currentRoute.includes('/pp/') ? 'Clube Orbitta' : 'Loja';
  }

  /**
   * Lista "Produto Sugerido" do contexto atual, com o local de exibição no nome
   * ("Produto Sugerido - Home", "Produto Sugerido - Clube Orbitta - Marcas"...).
   * Sem o sufixo todas as vitrines viram uma lista só no relatório.
   */
  private getSuggestedListContext(isPointsProgram: boolean, pagePath = this.placementPath()): SuggestedListContext {
    const base = this.mapper.defaultSuggestedListContext(isPointsProgram);
    const placement = suggestedListPlacement(pagePath);
    return placement ? { ...base, name: `${base.name} - ${placement}` } : base;
  }

  /**
   * Router é a fonte da rota; o último page_view pode ainda ser da tela anterior.
   */
  private placementPath(): string {
    return this.router.url.split(/[?#]/)[0];
  }

  private withSuggestedItemContext(item: GaItem, list: SuggestedListContext): GaItem {
    return {
      ...item,
      item_list_id: list.id,
      item_list_name: list.name,
      recommendation_source_item: list.source,
      item_origin: SUGGESTED_LIST.ITEM_ORIGIN,
    };
  }

  /**
   * Memoriza, item a item, de qual vitrine o produto saiu. É o que faz o
   * carrinho e o purchase manterem `item_list_name`/`recommendation_source_item`
   * do momento em que o produto foi adicionado.
   */
  private rememberSuggestedList(products: Product[], list: SuggestedListContext): void {
    products.forEach((product) => {
      this.mapper.rememberSuggestedListContext(this.mapper.productKeys(product), list);
    });
  }

  /** Registra o clique no card sugerido (vale por SUGGESTED_CLICK_TTL_MS). */
  private rememberSuggestedClick(product: Product, list: SuggestedListContext): void {
    const at = Date.now();
    this.mapper.productKeys(product).forEach((key) => this.suggestedClicks.set(key, { list, at }));
  }

  /** Lê e consome o clique recente no card sugerido, se ainda estiver na janela. */
  private takeSuggestedClick(product: Product): SuggestedListContext | undefined {
    const keys = this.mapper.productKeys(product);
    const now = Date.now();
    let found: SuggestedListContext | undefined;

    keys.forEach((key) => {
      const click = this.suggestedClicks.get(key);
      if (!click) return;
      this.suggestedClicks.delete(key);
      if (!found && now - click.at <= SUGGESTED_CLICK_TTL_MS) found = click.list;
    });

    return found;
  }


  /**
   * Dispara evento no Firebase Analytics / gtag.
   * Fire-and-forget: nunca bloqueia a UI, nunca lança exceção.
   * Pipeline único JS em web/Android/iOS — mesmos nomes e params.
   */
  private log(name: string, params?: Record<string, any>): void {
    const enriched = { ...(params ?? {}) };

    const ecommerceEvents = [
      'view_item_list', 'select_item', 'view_item',
      'add_to_cart', 'remove_from_cart', 'view_cart',
      'begin_checkout', 'add_shipping_info', 'add_payment_info', 'purchase',
      'view_promotion', 'select_promotion',
    ];
    if (ecommerceEvents.includes(name)) {
      enriched['catalog_origin'] = enriched['catalog_origin'] ?? this.getCatalogOrigin();
    }

    // Dimensão comum nos 3 ambientes (filtrar/comparar no GA4 sem eventos distintos).
    enriched['app_platform'] = this.platform;

    this.safeLog(name, this.mapper.sanitizeParams(enriched) as Record<string, any>);
  }

  private async safeLog(name: string, params: Record<string, any>): Promise<void> {
    try {
      if (!this.initialized) await this.init();
      console.log(
        `%c[GA4] ${name}`,
        'color: #4285F4; font-weight: bold;',
        params,
      );
      if (this.webGtagMode && typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', name, params);
        return;
      }
      // Web (Firebase JS) e nativo (SDK via bridge Capacitor): mesmo logEvent.
      await FirebaseAnalytics.logEvent({ name, params });
    } catch (err) {
      console.warn('[Analytics] logEvent falhou:', name, err);
    }
  }

  /** Mapeia path da rota para nome legível da tela. */
  private routeToScreenName(path: string): string {
    // ── Carrinho & Checkout ──
    if (path.startsWith('/tabs/cart/payment/summary')) return 'Checkout - Resumo do Pedido';
    if (path.startsWith('/tabs/cart/payment')) return 'Checkout - Pagamento';
    if (path.startsWith('/tabs/cart/editing')) return 'Carrinho - Edição';
    if (path.startsWith('/tabs/cart')) return 'Carrinho';

    // ── Catálogo (Loja) ──
    if (path.startsWith('/tabs/catalog/products/details')) return 'Catálogo - Detalhe do Produto';
    if (path.startsWith('/tabs/catalog/products')) return 'Catálogo - Produtos';
    if (path.startsWith('/tabs/catalog/brands')) return 'Catálogo - Marcas';
    if (path.startsWith('/tabs/catalog')) return 'Catálogo - Categorias';

    // ── Home ──
    if (path.startsWith('/tabs/home')) return 'Home';

    // ── Clube Orbitta ──
    if (path.startsWith('/tabs/pp/cart-redeem/summary')) return 'Clube - Checkout Resgate';
    if (path.startsWith('/tabs/pp/cart-redeem')) return 'Clube - Carrinho Resgate';
    if (path.startsWith('/tabs/pp/catalog/products/details')) return 'Clube - Detalhe do Produto';
    if (path.startsWith('/tabs/pp/catalog/products')) return 'Clube - Produtos';
    if (path.startsWith('/tabs/pp/catalog/brands')) return 'Clube - Marcas';
    if (path.startsWith('/tabs/pp/catalog')) return 'Clube - Categorias';
    if (path.startsWith('/tabs/pp/orbitta-categories')) return 'Clube - Categorias';
    if (path.startsWith('/tabs/pp/extract')) return 'Clube - Extrato';
    if (path.startsWith('/tabs/pp/orders/')) return 'Clube - Detalhe do Resgate';
    if (path.startsWith('/tabs/pp/orders')) return 'Clube - Meus Resgates';
    if (path.startsWith('/tabs/pp/rules')) return 'Clube - Regulamento';
    if (path.startsWith('/tabs/pp')) return 'Clube Orbitta';

    // ── Pedidos ──
    if (path.match(/^\/tabs\/orders\/\d/)) return 'Pedidos - Detalhe';
    if (path.startsWith('/tabs/orders')) return 'Pedidos';

    // ── Menu / Perfil ──
    if (path.startsWith('/tabs/menu/loaned-asset')) return 'Menu - Comodato';
    if (path.startsWith('/tabs/menu/financial')) return 'Menu - Financeiro';
    if (path.startsWith('/tabs/menu/business')) return 'Menu - Dados do Negócio';
    if (path.startsWith('/tabs/menu/config')) return 'Menu - Configurações';
    if (path.startsWith('/tabs/menu/cards')) return 'Menu - Cartões';
    if (path.startsWith('/tabs/menu/alerts')) return 'Menu - Alertas';
    if (path.startsWith('/tabs/menu/credit-limit')) return 'Menu - Limite de Crédito';
    if (path.startsWith('/tabs/menu/payment-slip/')) return 'Menu - Detalhe Boleto';
    if (path.startsWith('/tabs/menu/payment-slip')) return 'Menu - Boletos';
    if (path.startsWith('/tabs/menu/loan-historic')) return 'Menu - Histórico Empréstimos';
    if (path.startsWith('/tabs/menu/payment-debts')) return 'Menu - Débitos';
    if (path.startsWith('/tabs/menu')) return 'Menu';

    // ── Rotas top-level ──
    if (path === '/login') return 'Login';
    if (path === '/register') return 'Cadastro';
    if (path === '/recovery-password') return 'Recuperar Senha';
    if (path === '/walkthrough') return 'Walkthrough';
    if (path === '/notifications') return 'Notificações';
    if (path === '/qrcode') return 'QR Code';
    if (path === '/intro') return 'Intro';
    if (path === '/sfcoke-payment') return 'Pagamento SFCoke';
    if (path === '/change-information') return 'Alterar Dados';
    if (path === '/blocked-outdated') return 'Versão Desatualizada';
    if (path === '/maintenance') return 'Manutenção';

    return path.replace(/^\//, '').replace(/\//g, ' - ') || 'Root';
  }
}
